import React, { useEffect, useMemo, useState } from "react";
import { useMatches, useNavigate } from "react-router-dom";
import { MatchHandle } from "models/MatchHandleType";
import styled, { css } from "styled-components";
import { OCTOLogo } from "assets";
import GnbItemMenu from "./GnbItemMenu/GnbItemMenu";
import Divider from "components/foundation/Divider";
import GnbItemUpload from "./GnbItemUpload/GnbItemUpload";
import GnbItemProfile from "./GnbItemProfile";
import GnbPushNotification from "./GnbPushNotification";
import { LeftIcon } from "assets/icons";
import { ROUTER_PATH } from "config/path";
import ButtonDropdown from "components/foundation/ButtonDropdown/ButtonDropdown";
import { useLogoutMutation } from "features/auth/hooks/mutations";
import Modal from "components/foundation/Modal/Modal";
import { useSetRecoilState } from "recoil";
import { isLoggedOutState } from "store/auth";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import { useQueryClient } from "@tanstack/react-query";
import api, { logout } from "apis";
import useMyDeskNotificationQuery from "features/my-page/queries/useMyDeskNotificationQuery";
import {
  ADMIN_TYPES,
  DOCUMENT_CATEGORY_UUID,
  LOCAL_TIME_FORMAT,
} from "config/constant";
import dayjs from "dayjs";
import { useGetUnreadPushNotificationCount } from "features/pushNotification/queries";

const GnbHeader = () => {
  const matches = useMatches();
  const match = matches[matches.length - 1];

  const main = ["/"].includes(match.pathname);
  const handle = match?.handle as MatchHandle;
  const title = handle?.title || "";

  const navigate = useNavigate();

  /* 유저 정보 */
  const {
    data: currentUser,
    isInitialLoading: isUserInitalLoading,
    isSuccess: isUserSuccess,
  } = useUserDetailQuery();

  const isAdmin = useMemo(() => {
    return (
      currentUser?.adminType && ADMIN_TYPES.includes(currentUser.adminType)
    );
  }, [currentUser]);

  const isRegularMember = useMemo(() => {
    return currentUser?.regularMember;
  }, [currentUser]);

  /* 유저 프로필 */
  const CURRENT_DATE = useMemo(() => dayjs().format(LOCAL_TIME_FORMAT), []);

  const { data: hasNewMyDeskInfo } = useMyDeskNotificationQuery(
    {
      lastCheckedAt: CURRENT_DATE,
    },
    isUserSuccess,
  );

  /* 로그아웃 */
  const logoutMutation = useLogoutMutation();
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const setIsLoggedOut = useSetRecoilState(isLoggedOutState);

  const queryClient = useQueryClient();

  const onClickLogoutButton = () => {
    logoutMutation.mutate(
      {},
      {
        onSuccess: () => {
          logout();
          setIsLoggedOut(true);
          queryClient.removeQueries({ queryKey: api.user.getUserDetailKey() });
          navigate(ROUTER_PATH.auth.signIn);
        },
      },
    );
  };

  const notNormalUser = !!currentUser?.authorityDto;

  /**
   * 정상 로그인 여부에 따라 홈화면 라우트 시키기 위한 useEffect
   */
  useEffect(() => {
    // 초기 로딩 중이 아닌데 현재 유저 데이터가 존재하지 않는 경우
    if (!isUserInitalLoading && !currentUser) {
      // 홈 화면으로 이동
      navigate(ROUTER_PATH.auth.signIn);
    }
  }, [isUserInitalLoading, currentUser, navigate]);

  /* 안 읽은 푸시 알림 */
  const { data: unreadPushNotificationCount } =
    useGetUnreadPushNotificationCount({
      referenceDate: CURRENT_DATE,
    });

  return (
    <>
      <HeaderStyle>
        <HeaderInnerStyle>
          <LeftMenuStyle>
            {!main && (
              <BackButtonStyle className="mobile-only">
                <LeftIcon className="mobile-only" />
              </BackButtonStyle>
            )}
            <PageNameStyle className="mobile-only">{title}</PageNameStyle>

            <LogoButtonStyle
              className="logo pc-only"
              onClick={() => {
                if (match.pathname === ROUTER_PATH.home) {
                  window.location.reload();
                } else {
                  navigate(ROUTER_PATH.home);
                }
              }}
            />
            <NavMenuStyle className="pc-only">
              {notNormalUser && (
                <>
                  <GnbItemMenu href={ROUTER_PATH.document.work.dashboard}>
                    WORK
                  </GnbItemMenu>
                  <GnbItemMenu href={ROUTER_PATH.document.analysis.dashboard}>
                    ANALYSIS
                  </GnbItemMenu>
                </>
              )}
              <GnbItemMenu href={ROUTER_PATH.document.archive.dashboard}>
                ARCHIVES
              </GnbItemMenu>

              <Divider orientation="vertical" length={16} />

              {notNormalUser && (
                <GnbItemMenu emphatic href={ROUTER_PATH.notice.dashboard._path}>
                  NOTICE
                </GnbItemMenu>
              )}
              <GnbItemMenu
                emphatic
                href={
                  notNormalUser
                    ? ROUTER_PATH.myPage.desk
                    : `${ROUTER_PATH.myPage.desk}?selectedTab.uuid=${DOCUMENT_CATEGORY_UUID.ARCHIVES}&selectedTab.name=ARCHIVES&page=1`
                }
              >
                MY DESK
              </GnbItemMenu>
            </NavMenuStyle>
          </LeftMenuStyle>

          {currentUser && (
            <UserMenuStyle className="pc-only">
              <li>
                <ButtonDropdown
                  list={[
                    ...(notNormalUser
                      ? [
                          {
                            label: "WORK",
                            onClick: () => {
                              window.location.href =
                                ROUTER_PATH.document.work.create._path;
                            },
                          },
                          {
                            label: "ANALYSIS",
                            onClick: () => {
                              window.location.href =
                                ROUTER_PATH.document.analysis.create._path;
                            },
                          },
                        ]
                      : []),
                    {
                      label: "ARCHIVES",
                      onClick: () => {
                        window.location.href =
                          ROUTER_PATH.document.archive.create._path;
                      },
                    },
                  ]}
                >
                  <GnbItemUpload />
                </ButtonDropdown>
              </li>

              <li>
                <GnbPushNotification
                  onClick={() =>
                    (window.location.href = ROUTER_PATH.pushNotification._path)
                  }
                  hasNew={unreadPushNotificationCount?.data.result.count ?? 0}
                />
              </li>

              <li>
                <ButtonDropdown
                  list={[
                    {
                      label: "MY DESK",
                      to: ROUTER_PATH.myPage.desk,
                    },
                    {
                      label: "MY INFORMATION",
                      to: ROUTER_PATH.myPage.information,
                    },
                    ...(isAdmin
                      ? [
                          {
                            label: "USER MANAGEMENT",
                            to: ROUTER_PATH.user.dashboard._path,
                          },
                        ]
                      : []),
                    ...(isAdmin || isRegularMember
                      ? [
                          {
                            label: "TAG MANAGEMENT",
                            to: ROUTER_PATH.tag.dashboard._path,
                          },
                        ]
                      : []),
                    {
                      label: "LOG OUT",
                      onClick: () => setOpenLogoutModal(true),
                    },
                  ]}
                >
                  <GnbItemProfile
                    name={currentUser.name}
                    isNew={hasNewMyDeskInfo}
                  />
                </ButtonDropdown>
              </li>
            </UserMenuStyle>
          )}
        </HeaderInnerStyle>
      </HeaderStyle>

      <Modal.Root open={openLogoutModal} onOpenChange={setOpenLogoutModal}>
        <Modal.Body
          onConfirm={onClickLogoutButton}
          onCancel={() => setOpenLogoutModal(false)}
        >
          정말 로그아웃하시겠습니까?
        </Modal.Body>
      </Modal.Root>
    </>
  );
};

export default GnbHeader;

const HeaderStyle = styled.header`
  display: flex;
  justify-content: center;
  padding: 0 4rem;
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.zIndex.gnbHeader};
  background: ${({ theme }) => theme.color.background.white};
`;

const HeaderInnerStyle = styled.div`
  background: ${({ theme }) => theme.color.background.white};
  width: 100%;
  max-width: ${({ theme }) => theme.layout.desktop.contents.width};
  box-sizing: border-box;
  height: 7.8rem;

  display: flex;
  justify-content: space-between;
  align-items: center;

  svg.logo {
    color: ${({ theme }) => theme.color.primary};
    width: 8rem;
    height: 2rem;
  }
`;

const LeftMenuStyle = styled.div`
  display: flex;
  align-items: center;
  gap: 7rem;

  ${({ theme: { media } }) => css`
    ${media.small} {
      gap: 0.4rem;
    }
  `}
`;

const LogoButtonStyle = styled(OCTOLogo)`
  cursor: pointer;
`;

const NavMenuStyle = styled.ul`
  display: inline-flex;
  align-items: center;
  gap: 3rem;
`;

const UserMenuStyle = styled.ul`
  list-style: none;
  display: inline-flex;
  gap: 1.6rem;

  li {
    position: relative;
  }
`;

/* Mobile */
const BackButtonStyle = styled.button`
  display: inline-flex;
  align-items: center;
`;

const PageNameStyle = styled.h1`
  ${({ theme }) => theme.typography.body1.medium}
`;
